import type { ProductCardData } from "@models/product";
import type { VendorSku } from "@models/vendor";
import type { ShoppingCartItem } from "@models/shoppingCart";

// Both cart rails show at most this many products.
const RAIL_SIZE = 6;

export interface CartVendor {
    vendorId: number;
    vendorName: string;
}

// Picks the vendor backing the most cart lines. Ties resolve to the vendor of
// the first cart line (stable: only a strictly higher count displaces the
// incumbent, and iteration starts from the first line's vendor).
export const selectMostFrequentVendor = (
    items: ShoppingCartItem[],
): CartVendor | null => {
    if (items.length === 0) {
        return null;
    }

    const counts = new Map<number, number>();
    for (const item of items) {
        counts.set(item.vendorId, (counts.get(item.vendorId) ?? 0) + 1);
    }

    let winner = items[0];
    let winningCount = counts.get(winner.vendorId) ?? 0;
    for (const item of items) {
        const count = counts.get(item.vendorId) ?? 0;
        if (count > winningCount) {
            winner = item;
            winningCount = count;
        }
    }

    return { vendorId: winner.vendorId, vendorName: winner.vendorName };
};

// Reshapes vendor-SKU rows (SKU grain) to product-grain cards: one card per
// product id (first row wins), excluding products already in the cart.
export const reshapeVendorSkus = (
    rows: VendorSku[],
    cartProductIds: Set<string>,
): ProductCardData[] => {
    const byProduct = new Map<string, ProductCardData>();

    for (const row of rows) {
        // `product` is nullable on the wire; a row without it has no card to
        // render, so skip it rather than dereference undefined.
        if (!row.product) {
            continue;
        }
        if (cartProductIds.has(row.productId) || byProduct.has(row.productId)) {
            continue;
        }
        byProduct.set(row.productId, {
            id: row.productId,
            name: row.product.name,
            price: row.price,
            images: row.product.images,
        });
    }

    return Array.from(byProduct.values()).slice(0, RAIL_SIZE);
};
